import React, {useCallback, useMemo, useState} from 'react';
import {EmptyState, Modal, SecondaryButton, SkeletonLoader} from 'components';
import {flowsNav, reverseCircle} from 'assets/images/images';
import {useQuery} from 'react-query';
import {fetchAllVersionHistory} from '../../../../../requests/queries/flows';
import {useNavigate, useParams} from 'react-router-dom';
import moment from 'moment';
import {PAGES_URL} from '../../../../../helpers/constants';
const historyLimit = 5;

function VersionHistory({open, setOpen}) {
  const navigate = useNavigate();
  const [next, setNext] = useState(historyLimit);
  const {slug} = useParams();

  const {data: versionHistory, isLoading} = useQuery(
    ['all-version-history', slug],
    () => fetchAllVersionHistory(slug),
    {
      enabled: open,
    },
  );

  const versionData = useMemo(
    () => (!isLoading && versionHistory?.data) || [],
    [isLoading, versionHistory],
  );

  const handleLoadMore = () => {
    setNext(next + historyLimit);
  };

  const handleOpenVersion = useCallback(
    versionNumber => {
      if (versionNumber) {
        navigate(
          PAGES_URL.FLOWS_VERSION_RULES.replace(':slug', slug).replace(
            ':version',
            versionNumber,
          ),
        );
      }
    },
    [navigate, slug],
  );

  const currentVersion = useMemo(
    () => (!isLoading && versionData?.length > 0 && versionData[0]) || {},
    [isLoading, versionData],
  );

  return (
    <Modal
      show={open}
      onClose={() => setOpen(false)}
      extraModalClasses="max-w-[394px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="justify-center sm:justify-end"
      modalTitle="Version History"
    >
      <div>
        <div className="relative mt-6 px-6">
          {!isLoading && versionData.length === 0 ? (
            <div className="flex flex-col items-center w-full mb-6 justify-between">
              <EmptyState
                noBtn
                heading="No Version History"
                height={25}
                body="No version history found for current flow"
              />
            </div>
          ) : (
            <>
              {/* <div
                className="absolute left-[23px] top-0 w-[1px] bg-batchBlue20"
                style={{
                  height: 'calc(100% - 120px)',
                }}
              ></div> */}

              <div className="flex items-start mb-9">
                <div className="relative z-10 w-[46px] h-[46px] rounded-full flex items-center justify-center bg-batchBlue20">
                  <span className="w-[28px] h-[28px] bg-brandBlue rounded-full" />
                </div>

                <div className="flex flex-col ml-2">
                  <p className="text-base text-[#0B1C56] font-medium text-start">
                    Current version
                  </p>
                  <p className="text-[12px] text-[#858DAA]">
                    {moment(currentVersion?.createdAt).format(
                      'Do MMM YYYY hh:mm a',
                    )}
                  </p>
                </div>
              </div>
            </>
          )}

          {isLoading &&
            [1, 2, 3, 4].map(d => (
              <div
                key={d}
                className="flex items-center w-full mb-6 justify-between"
              >
                <div className="min-w-[46px] min-h-[46px] flex items-center justify-center">
                  <div className="relative z-10 w-[34px] h-[34px] rounded-full flex items-center justify-center bg-[#0B1C56]">
                    <img src={flowsNav} alt="flow icon" />
                  </div>
                </div>

                <div>
                  <SkeletonLoader
                    style={{
                      width: '100%',
                      height: 20,
                      maxWidth: 150,
                      margin: 0,
                    }}
                  />
                </div>
                <div className="ml-6">
                  <SkeletonLoader
                    style={{
                      width: '100%',
                      height: 30,
                      maxWidth: 100,
                      margin: 0,
                      borderRadius: 6,
                    }}
                  />
                </div>
              </div>
            ))}

          {versionData.length > 1 &&
            versionData?.slice(1, next)?.map(flow => (
              <div key={flow.id} className="flex items-center w-full mb-6">
                <div className="min-w-[46px] min-h-[46px] flex items-center justify-center">
                  <div className="relative z-10 w-[34px] h-[34px] rounded-full flex items-center justify-center bg-[#0B1C56]">
                    <img src={flowsNav} alt="flow icon" />
                  </div>
                </div>

                <div className="flex items-center justify-between ml-2 w-full">
                  <div className="flex flex-col gap-1">
                    <p className="text-base text-[#0B1C56] font-medium text-start">
                      Version {flow?.version_number}
                    </p>
                    <p className="text-[12px] text-[#858DAA]">
                      {moment(flow.createdAt).format('Do MMM YYYY hh:mm a')}
                    </p>
                  </div>
                  <SecondaryButton
                    onClick={() => handleOpenVersion(flow.version_number)} // Passing version_number instead of id
                    buttonText="View Version"
                    className="!h-9 flex items-center !text-[12px] justify-center !outline-[#DFE1E7] !text-[#033971] flex-row-reverse gap-1"
                  />
                </div>
              </div>
            ))}
        </div>

        {next < versionData?.length && (
          <div className="w-full mt-6 px-6 py-[14px] border-[0.5px] flex gap-4 border-transparent border-t-[#EFEFEF]">
            <SecondaryButton
              buttonText="View More History"
              className="!h-10 w-full"
              src={reverseCircle}
              onClick={handleLoadMore}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default VersionHistory;
